import React from "react";
import { observer } from "mobx-react";
import { observable } from "mobx";
import { RouteComponentProps, withRouter } from "react-router-dom";

import { Button } from "@material-ui/core";

import InAppStudioStore, {
  InAppStudioArchivedStore,
} from "stores/InAppStudioStore";
import InAppNutritionStore, {
  InAppNutritionArchivedStore,
} from "stores/InAppNutritionStore";
import InAppResourcesStore, {
  InAppResourcesArchivedStore,
} from "stores/InAppResourcesStore";
import ContentStudioWorkoutsStore, {
  ContentStudioArchivedWorkoutsStore,
} from "stores/ContentStudioWorkoutsStore";
import ContentStudioEquipmentStore, {
  ContentStudioArchivedEquipmentStore,
} from "stores/ContentStudioEquipmentStore";
import ContentStudioMovementsStore, {
  ContentStudioArchivedMovementsStore,
} from "stores/ContentStudioMovementsStore";
import ContentNutritionRecipesStore, {
  ContentNutritionArchivedRecipesStore,
} from "stores/ContentNutritionRecipesStore";
import ContentResourcesQuickLinksStore, {
  ContentStudioArchivedQuickLinksStore,
} from "stores/ContentResourcesQuickLinksStore";

import SvgI24Archive from "assets/icons/I24Archive";

import RestoreArchivedForm from "components/RestoreArchivedForm";
import ConfirmationDialog from "components/ConfirmationDialog";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import { SNACKBAR_TYPES } from "components/Snackbar";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import { RestoreTableStoreType } from "stores/templates/TableStore";

import ROUTES from "navigation/routes";

type Props = {} & RouteComponentProps & GlobalPopupProps & SnackbarContextProps;

@observer
class RestoreArchivedButton extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;

  get data(): {
    mainStore: any;
    archiveStore: RestoreTableStoreType;
    placeholderText: string;
    withFilter?: boolean;
    withAccessProgramFilter?: boolean;
  } {
    const { pathname } = this.props.location;
    switch (pathname) {
      case ROUTES.IN_APP_STUDIO:
        return {
          mainStore: InAppStudioStore,
          archiveStore: InAppStudioArchivedStore,
          placeholderText: "No categories archived yet.",
          withFilter: true,
          withAccessProgramFilter: true,
        };
      case ROUTES.IN_APP_NUTRITION:
        return {
          mainStore: InAppNutritionStore,
          archiveStore: InAppNutritionArchivedStore,
          placeholderText: "No categories archived yet.",
          withFilter: true,
          withAccessProgramFilter: true,
        };
      case ROUTES.IN_APP_RESOURCES:
        return {
          mainStore: InAppResourcesStore,
          archiveStore: InAppResourcesArchivedStore,
          placeholderText: "No categories archived yet.",
          withFilter: true,
          withAccessProgramFilter: true,
        };
      case ROUTES.CONTENT_STUDIO_WORKOUTS:
        return {
          mainStore: ContentStudioWorkoutsStore,
          archiveStore: ContentStudioArchivedWorkoutsStore,
          placeholderText: "No workouts archived yet.",
          withFilter: true,
          withAccessProgramFilter: false,
        };
      case ROUTES.CONTENT_STUDIO_EQUIPMENT:
        return {
          mainStore: ContentStudioEquipmentStore,
          archiveStore: ContentStudioArchivedEquipmentStore,
          placeholderText: "No equipment archived yet.",
          withFilter: false,
        };
      case ROUTES.CONTENT_STUDIO_MOVEMENTS:
        return {
          mainStore: ContentStudioMovementsStore,
          archiveStore: ContentStudioArchivedMovementsStore,
          placeholderText: "No movements archived yet.",
          withFilter: false,
        };
      case ROUTES.CONTENT_NUTRITION_RECIPES:
        return {
          mainStore: ContentNutritionRecipesStore,
          archiveStore: ContentNutritionArchivedRecipesStore,
          placeholderText: "No recipes archived yet.",
          withFilter: true,
          withAccessProgramFilter: false,
        };
      case ROUTES.CONTENT_RESOURCES_QUICK_LINKS:
        return {
          mainStore: ContentResourcesQuickLinksStore,
          archiveStore: ContentStudioArchivedQuickLinksStore,
          placeholderText: "No quick links archived yet.",
          withFilter: true,
          withAccessProgramFilter: false,
        };
    }
  }

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  onShowArchive = () => {
    const { openPopup } = this.props;

    openPopup({
      key: "RestoreArchivedForm",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      confirmation: {
        renderConfirmation: ({ deny, allow }) => (
          <ConfirmationDialog
            title="Discard Changes"
            text="Are you sure you would like to discard changes? All information will be lost."
            declineText="Cancel"
            acceptText="Discard"
            onDecline={deny}
            onAccept={allow}
          />
        ),
        shouldShowConfirmation: this.shouldShowConfirmation,
      },
      render: ({ close }) => (
        <RestoreArchivedForm
          store={this.data?.archiveStore}
          placeholderText={this.data.placeholderText}
          onChangeIsDirty={this.onChangeIsPopupDirty}
          onClose={close}
          onSubmit={this.restoreArchivedItems(close)}
          isLoading={this.data?.archiveStore?.isManaging}
          withFilter={this.data.withFilter}
          withAccessProgramFilter={this.data.withAccessProgramFilter}
        />
      ),
    });
  };

  restoreArchivedItems = (close: () => void) => async (values: any) => {
    const { setMessage } = this.props;
    const { ids } = values;

    const payload = { ids: ids?.map(({ id }: { id: number }) => id) };

    try {
      await this.data?.archiveStore.restore(payload);
      this.resetIsPopupDirty();
      close();
      this.data?.mainStore.resetPage();
      this.data?.mainStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  shouldShowConfirmation = () => {
    return this.isPopupDirty;
  };

  render() {
    return (
      <Button
        variant="outlined"
        color="primary"
        startIcon={<SvgI24Archive />}
        onClick={this.onShowArchive}
      >
        Show Archive
      </Button>
    );
  }
}

export default withGlobalPopup(
  withGlobalSnackbar(withRouter(RestoreArchivedButton)),
);
